/**
 * parseSpokenTime.js — Mar 3, 2026
 *
 * Convert spoken time phrases (Deepgram transcription output) into a
 * 24-hour "HH:MM" string, or null if unparseable.
 *
 * Supports the phrasings a busy provider actually uses:
 *   • "eleven forty-five"       → "11:45"
 *   • "eleven forty five"       → "11:45"
 *   • "11:45"                   → "11:45"
 *   • "1145"                    → "11:45"
 *   • "11 45"                   → "11:45"
 *   • "nine thirty am"          → "09:30"
 *   • "two forty five pm"       → "14:45"
 *   • "oh nine hundred"         → "09:00"
 *   • "zero nine hundred"       → "09:00"
 *   • "nine hundred hours"      → "09:00"
 *   • "fourteen thirty"         → "14:30"
 *   • "twenty three hundred"    → "23:00"
 *   • "noon"                    → "12:00"
 *   • "midnight"                → "00:00"
 *   • "eleven o'clock"          → "11:00"
 *
 * Deliberately conservative — returns null on ambiguity so the caller can
 * surface a "didn't catch that" toast instead of silently writing wrong
 * data into a clinical note.
 */

const NUMBER_WORDS = {
  zero: 0, oh: 0, o: 0,
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
  sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20,
  thirty: 30, forty: 40, fifty: 50,
};

// Convert compound number-words in a token stream to numeric strings.
// Handles "twenty three" → "23", "forty five" → "45", but leaves "twenty"
// alone if the next token isn't 1-9.
function coalesceNumberWords(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const n = NUMBER_WORDS[t];
    if (n === undefined) { out.push(t); continue; }

    // Try to combine tens + ones (e.g. "twenty" + "three" → 23).
    if ([20, 30, 40, 50].includes(n)) {
      const next = tokens[i + 1];
      const nextN = NUMBER_WORDS[next];
      if (nextN !== undefined && nextN >= 1 && nextN <= 9) {
        out.push(String(n + nextN));
        i += 1;
        continue;
      }
    }
    out.push(String(n));
  }
  return out;
}

export function parseSpokenTime(raw) {
  if (!raw || typeof raw !== 'string') return null;

  // Normalize: lowercase, strip trailing punctuation, collapse whitespace.
  // Keep ':' because "11:45" should parse directly.
  let text = raw.toLowerCase().trim();
  text = text.replace(/[.,!?"]+/g, ' ');
  text = text.replace(/[-–—]/g, ' ');   // "forty-five" → "forty five"
  text = text.replace(/o['`’]clock/g, ' oclock ');
  text = text.replace(/\s+/g, ' ').trim();
  if (!text) return null;

  // Special keywords first.
  if (/\bnoon\b/.test(text)) return '12:00';
  if (/\bmidnight\b/.test(text)) return '00:00';

  // Direct HH:MM match anywhere in text (e.g. "at 11:45").
  const colonMatch = text.match(/\b(\d{1,2}):(\d{2})\b/);
  if (colonMatch) {
    return normalizeHM(parseInt(colonMatch[1]), parseInt(colonMatch[2]), pickAmPm(text));
  }

  // 4-digit military e.g. "1145" or "0730".
  const fourDigit = text.match(/\b(\d{4})\b/);
  if (fourDigit) {
    const d = fourDigit[1];
    const h = parseInt(d.slice(0, 2));
    const m = parseInt(d.slice(2, 4));
    return normalizeHM(h, m, null); // 4-digit already 24hr, ignore am/pm
  }

  // 3-digit military e.g. "730" → 07:30.
  const threeDigit = text.match(/\b(\d{3})\b/);
  if (threeDigit) {
    const d = threeDigit[1];
    const h = parseInt(d.slice(0, 1));
    const m = parseInt(d.slice(1, 3));
    return normalizeHM(h, m, null);
  }

  // Two separated integers e.g. "11 45".
  const twoNums = text.match(/\b(\d{1,2})\s+(\d{2})\b/);
  if (twoNums) {
    return normalizeHM(parseInt(twoNums[1]), parseInt(twoNums[2]), pickAmPm(text));
  }

  // Word-based path. Split → coalesce compound tens ("twenty three" → 23).
  const tokens = text.split(/\s+/);
  const merged = coalesceNumberWords(tokens);

  // "hundred" & "hundred hours" → HH:00
  // Grab the numbers immediately before "hundred".
  const hundredIdx = merged.findIndex((t) => t === 'hundred');
  if (hundredIdx > 0) {
    // Pull up to 2 preceding numeric tokens.
    const prev = merged.slice(Math.max(0, hundredIdx - 2), hundredIdx)
      .filter((t) => /^\d+$/.test(t));
    if (prev.length) {
      // "oh nine" → [0, 9] → 9. "twenty three" already coalesced to "23".
      let h;
      if (prev.length === 2) {
        // e.g. "0 9" → 09 (concatenate as string then int).
        h = parseInt(prev.join(''));
      } else {
        h = parseInt(prev[0]);
      }
      return normalizeHM(h, 0, null);
    }
  }

  // "eleven forty five" style — pick first two numeric tokens.
  const nums = merged.filter((t) => /^\d+$/.test(t)).map(Number);
  if (nums.length >= 2) {
    // "eleven o'clock" collapses to [11] via NUMBER_WORDS not matching
    // "oclock", so it takes the general path here; guard with token check.
    const hasOclock = merged.includes('oclock');
    if (hasOclock) {
      return normalizeHM(nums[0], 0, pickAmPm(text));
    }
    return normalizeHM(nums[0], nums[1], pickAmPm(text));
  }
  if (nums.length === 1) {
    // Single number → treat as top-of-the-hour.
    return normalizeHM(nums[0], 0, pickAmPm(text));
  }

  return null;
}

function pickAmPm(text) {
  // Detect AM/PM (also "a m" / "p m" from Deepgram spacing).
  if (/\b(pm|p m|p\.m|in the (?:afternoon|evening|night))\b/.test(text)) return 'pm';
  if (/\b(am|a m|a\.m|in the morning)\b/.test(text)) return 'am';
  return null;
}

function normalizeHM(hRaw, mRaw, ampm) {
  let h = Number(hRaw);
  let m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;

  // Apply AM/PM only when hour is 1-12 (24hr hours ignore it).
  if (ampm && h >= 1 && h <= 12) {
    if (ampm === 'pm' && h < 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
  }

  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;

  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default parseSpokenTime;
